import { type ReactNode, useId } from "react";
import type { Control, FieldPath, FieldValues } from "react-hook-form";
import { FieldTooltip } from "@/shared/components/form/field-tooltip";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import { cn } from "@/shared/lib/cn";

interface TextFieldProps<TValues extends FieldValues> {
  control: Control<TValues>;
  name: FieldPath<TValues>;
  label: string;
  /// `id`-less by default; `<FormItem>` provides a stable id via context.
  type?: "text" | "email" | "url" | "tel" | "password" | "number" | "search" | "date" | "time";
  placeholder?: string;
  /// Quiet hint below the field. Hidden when an error is present.
  hint?: string;
  tooltip?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  inputMode?: "text" | "email" | "url" | "tel" | "numeric" | "decimal" | "search";
  /**
   * Store the value as a NUMBER rather than the string the DOM gives back.
   *
   * `type="number"` alone still hands react-hook-form a string, so a
   * `z.number()` schema rejects every keystroke. An empty box becomes `NaN`,
   * not 0: a half-typed field is invalid input, and coercing it to zero is how
   * a blank silently becomes a deliberate threshold of nothing.
   */
  valueAsNumber?: boolean;
  autoComplete?: string;
  /// Optional leading icon (lucide). Renders inside the input padding.
  leadingIcon?: ReactNode;
  /// Inline autocomplete suggestions rendered via native `<datalist>`.
  /// Users can still type freely; this just surfaces known values
  /// (e.g. existing todo groups). De-duped automatically.
  suggestions?: ReadonlyArray<string>;
  className?: string;
  inputClassName?: string;
}

/// `NaN` is what an empty numeric input holds; rendering it would print the
/// literal "NaN" in the box, so it shows empty and stays invalid until typed.
function numberDisplayValue(value: unknown, valueAsNumber: boolean | undefined): string {
  if (valueAsNumber && typeof value === "number" && Number.isNaN(value)) return "";
  return (value ?? "") as string;
}

/// Single-line text input wired to react-hook-form via `<FormField>`.
/// Labels, errors, and `aria-invalid`/`aria-describedby` are managed for you.
export function TextField<TValues extends FieldValues>({
  control,
  name,
  label,
  type = "text",
  placeholder,
  hint,
  tooltip,
  disabled,
  autoFocus,
  inputMode,
  autoComplete,
  valueAsNumber,
  leadingIcon,
  suggestions,
  className,
  inputClassName,
}: TextFieldProps<TValues>): ReactNode {
  const listId = useId();
  const dedupedSuggestions = suggestions
    ? Array.from(new Set(suggestions.filter((s) => s.length > 0)))
    : undefined;
  const hasSuggestions = dedupedSuggestions && dedupedSuggestions.length > 0;
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        /* FormControl is a Slot that puts the id, aria-describedby and aria-invalid on
           its immediate child, and <FormLabel htmlFor> points at that id. It therefore
           wraps the <Input> itself on both branches; wrapping the leading-icon div
           instead pointed the label at a div and left the input unnamed. */
        const input = (
          <FormControl>
            <Input
              type={type}
              placeholder={placeholder}
              disabled={disabled}
              autoFocus={autoFocus}
              inputMode={inputMode}
              autoComplete={autoComplete}
              list={hasSuggestions ? listId : undefined}
              className={cn(leadingIcon ? "pl-9" : undefined, inputClassName)}
              {...field}
              value={numberDisplayValue(field.value, valueAsNumber)}
              onChange={(event) =>
                field.onChange(valueAsNumber ? event.target.valueAsNumber : event.target.value)
              }
            />
          </FormControl>
        );

        return (
          <FormItem className={className}>
            <div className="flex items-center gap-1.5">
              <FormLabel>{label}</FormLabel>
              {tooltip ? <FieldTooltip term={label} content={tooltip} /> : null}
            </div>
            {leadingIcon ? (
              <div className="relative">
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary [&_svg]:size-4"
                >
                  {leadingIcon}
                </span>
                {input}
              </div>
            ) : (
              input
            )}
            {hasSuggestions ? (
              <datalist id={listId}>
                {dedupedSuggestions.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
            ) : null}
            {hint ? <FormDescription>{hint}</FormDescription> : null}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
